import type { MetaFunction } from '@remix-run/node';

import type { StaticPageLoaderData, staticPageLoader } from '~/services/static_page';

import getCanonicalUrl from '../getCanonicalUrl';
import getMergedMetaData from '../getMergedMetaData';
import getMetaDataLinks from '../getMetaDataLinks';

function getStructuredDataByPage(pageSlug: string, loaderData: StaticPageLoaderData) {
  switch (pageSlug) {
    case '/archive':
      return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            item: {
              '@id': loaderData?.site?.publication?.url ?? '',
              name: loaderData?.site?.publication?.name ?? '',
            },
          },
        ],
      };
    default:
      return {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        url: loaderData?.site?.publication?.url ?? '',
      };
  }
}

interface DynamicMetaDataOverrides {
  title?: string;
  description?: string;
}

const getStaticPageMetaData = (
  { matches, data }: Parameters<MetaFunction<typeof staticPageLoader>>[0],
  overrideData?: DynamicMetaDataOverrides,
) => {
  const requestUrl = data?.requestUrl;
  const baseUrl = data?.site?.publication?.url;
  const isHomePage = data?.page?.is_home_page ?? false;
  const canonicalUrl = isHomePage && baseUrl ? getCanonicalUrl(baseUrl) : getCanonicalUrl(requestUrl);

  let url: URL | null = null;
  let pageSlug: string;

  try {
    url = requestUrl ? new URL(requestUrl) : null;
    pageSlug = url?.pathname || data?.pageSlug || '/';
  } catch {
    url = null;
    pageSlug = data?.pageSlug || '/';
  }

  const siteVersion = data?.site?.viewable_site_version;
  const siteMetadata = siteVersion?.metadata;
  const pageVersion = data?.page?.viewable_page_version;
  const pageMetadata = pageVersion?.metadata;

  const getMetadata = (key: string) => {
    return pageMetadata?.[key] || siteMetadata?.[key] || '';
  };

  const publicationName = data?.site?.publication?.name || '';

  const pageHasExplicitSetting = pageMetadata?.append_publication_name_to_title !== undefined;
  const siteAppendSetting = siteMetadata?.append_publication_name_to_title ?? true;
  const appendPublicationName = pageHasExplicitSetting
    ? pageMetadata.append_publication_name_to_title
    : siteAppendSetting;

  const buildTitle = (title: string, fallback: string = publicationName) => {
    if (title && publicationName && appendPublicationName) {
      return `${title} | ${publicationName}`;
    }
    return title || fallback;
  };

  // Prioritize: page meta > page name > site meta (instead of getMetadata which checks site before page name)
  const pageTitle = (
    overrideData?.title ||
    pageMetadata?.meta_title ||
    data?.page?.name ||
    siteMetadata?.meta_title ||
    ''
  ).trim();
  const titleWithPublication = buildTitle(pageTitle);

  const ogTitle = (
    overrideData?.title ||
    pageMetadata?.facebook_title ||
    data?.page?.name ||
    siteMetadata?.facebook_title ||
    ''
  ).trim();
  const ogTitleWithPublication = buildTitle(ogTitle);

  const twitterTitle = (
    overrideData?.title ||
    pageMetadata?.x_title ||
    data?.page?.name ||
    siteMetadata?.x_title ||
    ''
  ).trim();
  const twitterTitleWithPublication = buildTitle(twitterTitle, titleWithPublication);

  return getMergedMetaData({
    canonicalUrl,
    matches,
    overrides: {
      ...(getMetadata('noindex_enabled') === true ? { robots: 'noindex' } : {}),
      title: titleWithPublication,
      description: getMetadata('meta_description') || pageVersion?.description,
      'og:title': ogTitleWithPublication,
      'og:description': getMetadata('facebook_description') || pageVersion?.description,
      'og:image': getMetadata('facebook_image_url'),
      'og:url': canonicalUrl,
      'twitter:title': twitterTitleWithPublication,
      'twitter:description': getMetadata('x_description'),
      'twitter:image': getMetadata('x_image_url'),
      'twitter:creator': getMetadata('x_username'),
      'twitter:url': canonicalUrl,
    },
    links: getMetaDataLinks({ renderableFonts: data?.renderableFonts || [] }),
    structuredData: getStructuredDataByPage(pageSlug, data) || {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      url: canonicalUrl,
      potentialAction: {
        '@type': 'SearchAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: `${canonicalUrl}?q={search_term_string}`,
        },
        'query-input': 'required name=search_term_string',
      },
    },
  });
};

export default getStaticPageMetaData;
